import {useSelector} from 'react-redux';
import {ForecastDaysMode} from '@/app/constants/forecast';
import {RouteName} from '@/app/constants/routing';
import {LocationType} from '@/common/constants/locationType';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {useRoute} from '@/common/services/router';
import {getRouteLanguage} from '@/app/routing/getRouteLanguage';
import {selectDomainConfig, selectLanguage} from '@/app/store/config/selectors';
import {selectForecastData} from '@/app/store/forecast/selectors';
import {selectMapsData} from '@/app/store/maps/selectors';
import domains from '@/domains.json';

function interpolate(template: string, values: Record<string, string>): string {
	return template.replace(/{(\w+)}/g, (_, key) => values[key] ?? '');
}

const months = {
	uk: [
		'січня',
		'лютого',
		'березня',
		'квітня',
		'травня',
		'червня',
		'липня',
		'серпня',
		'вересня',
		'жовтня',
		'листопада',
		'грудня',
	],
	ru: [
		'января',
		'февраля',
		'марта',
		'апреля',
		'мая',
		'июня',
		'июля',
		'августа',
		'сентября',
		'октября',
		'ноября',
		'декабря',
	],
	en: [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	],
	pl: [
		'Styczeń',
		'Luty',
		'Marzec',
		'Kwiecień',
		'Maj',
		'Czerwiec',
		'Lipiec',
		'Sierpień',
		'Wrzesień',
		'Październik',
		'Listopad',
		'Grudzień',
	],
	de: [
		'Januar',
		'Februar',
		'März',
		'April',
		'Mai',
		'Juni',
		'Juli',
		'August',
		'September',
		'Oktober',
		'November',
		'Dezember',
	],
};

const days = {
	uk: ['на неділю', 'на понеділок', 'на вівторок', 'на середу', 'на четвер', "на п'ятницю", 'на суботу'],
	ru: ['на воскресенье', 'на понедельник', 'на вторник', 'на среду', 'на четверг', 'на пятницу', 'на субботу'],
	en: ['for Sunday', 'for Monday', 'for Tuesday', 'for Wednesday', 'for Thursday', 'for Friday', 'for Saturday'],
	pl: ['na niedzielę', 'na poniedziałek', 'na wtorek', 'na środę', 'na czwartek', 'na piątek', 'na sobotę'],
	de: ['für Sonntag', 'für Montag', 'für Dienstag', 'für Mittwoch', 'für Donnerstag', 'für Freitag', 'für Samstag'],
};

const dayNames = {
	uk: ['неділя', 'понеділок', 'вівторок', 'середа', 'четвер', "п'ятниця", 'субота'],
	ru: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
	en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
	pl: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
	de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
};

const nearestWeekday = {
	uk: [
		'найближчої неділі',
		'найближчого понеділка',
		'найближчого вівторка',
		'найближчої середи',
		'найближчого четверга',
		"найближчої п'ятниці",
		'найближчої суботи',
	],
	ru: [
		'в ближайшее воскресенье',
		'в ближайший понедельник',
		'в ближайший вторник',
		'в ближайшую среду',
		'в ближайший четверг',
		'в ближайшую пятницу',
		'в ближайшую субботу',
	],
	en: [
		'this coming Sunday',
		'this coming Monday',
		'this coming Tuesday',
		'this coming Wednesday',
		'this coming Thursday',
		'this coming Friday',
		'this coming Saturday',
	],
	pl: [
		'w najbliższą niedzielę',
		'w najbliższy poniedziałek',
		'w najbliższy wtorek',
		'w najbliższą środę',
		'w najbliższy czwartek',
		'w najbliższy piątek',
		'w najbliższą sobotę',
	],
	de: [
		'am kommenden Sonntag',
		'am kommenden Montag',
		'am kommenden Dienstag',
		'am kommenden Mittwoch',
		'am kommenden Donnerstag',
		'am kommenden Freitag',
		'am kommenden Samstag',
	],
};

type Lang = 'uk' | 'ru' | 'en' | 'pl' | 'de';
type DomainName = keyof typeof domains;

export function useGetPageSeoMeta() {
	const {route} = useRoute<AnyRouteResult>();

	const {domainName} = useSelector(selectDomainConfig);
	const domainConfig = domains[domainName as DomainName];
	const lang = useSelector(selectLanguage) || getRouteLanguage(route) || domainConfig.primaryLanguage;
	const seo = domainConfig.seo;

	if (route.name === RouteName.map) {
		const {location, superior} = useSelector(selectMapsData)!;
		if (location.type === LocationType.Continent && 'mapContinent' in seo) {
			const weatherMapContinent = seo.mapContinent as Record<
				Lang,
				{title: string; description: string; keywords: string}
			>;
			const metaKey = weatherMapContinent[lang as Lang];
			return {
				title: interpolate(metaKey.title, {
					continentTitleIn: location.titleLc,
					continentTitleOf: location.titleGc || '',
				}),
				description: interpolate(metaKey.description, {
					continentTitleIn: location.titleLc,
					continentTitleOf: location.titleGc || '',
				}),
				descriptionOg: interpolate(metaKey.description, {
					continentTitleIn: location.titleLc,
					continentTitleOf: location.titleGc || '',
				}),
				keywords: interpolate(metaKey.keywords, {
					continentTitleIn: location.titleLc,
					continentTitleOf: location.titleGc || '',
					continentTitle: location.title,
				}),
			};
		} else if (location.type === LocationType.Country && 'mapCountry' in seo) {
			const weatherMapCountry = seo.mapCountry as Record<
				Lang,
				{title: string; description: string; keywords: string}
			>;
			const metaKey = weatherMapCountry[lang as Lang];
			return {
				title: interpolate(metaKey.title, {
					countryTitleIn: location.titleLc,
					countryTitleOf: location.titleGc || '',
					countryTitle: location.title,
				}),
				description: interpolate(metaKey.description, {
					countryTitleIn: location.titleLc,
					countryTitleOf: location.titleGc || '',
				}),
				descriptionOg: interpolate(metaKey.description, {
					countryTitleIn: location.titleLc,
					countryTitleOf: location.titleGc || '',
				}),
				keywords: interpolate(metaKey.keywords, {
					countryTitleIn: location.titleLc,
					countryTitleOf: location.titleGc || '',
					countryTitle: location.title,
				}),
			};
		} else if (location.type === LocationType.Region && 'mapRegion' in seo) {
			const weatherMapRegion = seo.mapRegion as Record<
				Lang,
				{title: string; description: string; keywords: string}
			>;
			const metaKey = weatherMapRegion[lang as Lang];
			return {
				title: interpolate(metaKey.title, {
					regionTitle: location.title,
					regionTitleIn: location.titleLc,
					countryTitleOf: superior.country?.titleGc || '',
					countryTitle: superior.country?.title || '',
				}),
				description: interpolate(metaKey.description, {
					regionTitleIn: location.titleLc,
					countryTitleOf: superior.country?.titleGc || '',
					regionTitle: location.title,
				}),
				descriptionOg: interpolate(metaKey.description, {
					regionTitleIn: location.titleLc,
					countryTitleOf: superior.country?.titleGc || '',
					regionTitle: location.title,
				}),
				keywords: interpolate(metaKey.keywords, {
					regionTitleIn: location.titleLc,
					countryTitleOf: superior.country?.titleGc || '',
					countryTitle: superior.country?.title || '',
					regionTitle: location.title,
				}),
			};
		} else if (location.type === LocationType.District && 'mapDistrict' in seo) {
			const weatherMapDistrict = seo.mapDistrict as Record<
				Lang,
				{title: string; description: string; keywords: string}
			>;
			const metaKey = weatherMapDistrict[lang as Lang];
			return {
				title: interpolate(metaKey.title, {
					districtTitleIn: location.titleLc,
					districtTitle: location.title,
				}),
				description: interpolate(metaKey.description, {
					districtTitleIn: location.titleLc,
					districtTitle: location.title,
					countryTitle: superior.country?.title || '',
					countryTitleOf: superior.country?.titleGc || '',
					regionTitle: superior.region?.title || '',
					regionTitleIn: superior.region?.titleLc || '',
				}),
				descriptionOg: interpolate(metaKey.description, {
					districtTitleIn: location.titleLc,
					districtTitle: location.title,
					countryTitle: superior.country?.title || '',
					countryTitleOf: superior.country?.titleGc || '',
					regionTitle: superior.region?.title || '',
					regionTitleIn: superior.region?.titleLc || '',
				}),
				keywords: interpolate(metaKey.keywords, {
					districtTitleIn: location.titleLc,
					countryTitle: superior.country?.title || '',
				}),
			};
		} else if (location.type === LocationType.SubDistrict && 'mapSubDistrict' in seo) {
			const weatherMapSubDistrict = seo.mapSubDistrict as Record<
				Lang,
				{title: string; description: string; keywords: string}
			>;
			const metaKey = weatherMapSubDistrict[lang as Lang];
			return {
				title: interpolate(metaKey.title, {
					districtTitle: superior.district?.title || '',
					districtTitleIn: superior.district?.titleLc || '',
					subDistrictTitle: location.title,
					subDistrictTitleIn: location.titleLc,
				}),
				description: interpolate(metaKey.description, {
					districtTitle: superior.district?.title || '',
					districtTitleIn: superior.district?.titleLc || '',
					countryTitle: superior.country?.title || '',
					countryTitleOf: superior.country?.titleGc || '',
					regionTitle: superior.region?.title || '',
					regionTitleIn: superior.region?.titleLc || '',
					subDistrictTitle: location.title,
					subDistrictTitleIn: location.titleLc,
				}),
				descriptionOg: interpolate(metaKey.description, {
					districtTitle: superior.district?.title || '',
					districtTitleIn: superior.district?.titleLc || '',
					countryTitle: superior.country?.title || '',
					countryTitleOf: superior.country?.titleGc || '',
					regionTitle: superior.region?.title || '',
					regionTitleIn: superior.region?.titleLc || '',
					subDistrictTitle: location.title,
					subDistrictTitleIn: location.titleLc,
				}),
				keywords: interpolate(metaKey.keywords, {
					countryTitle: superior.country?.title || '',
					subDistrictTitleIn: location.titleLc,
				}),
			};
		}
		return seo.weatherMain[lang as keyof typeof seo.weatherMain];
	}

	const {location} = useSelector(selectForecastData)!;

	if (
		route.name === RouteName.weather &&
		route.data.daysMode === ForecastDaysMode.Days7 &&
		route.data.selectedWeekday !== undefined &&
		'weatherByWeekday' in seo
	) {
		const weatherByWeekday = seo.weatherByWeekday as Record<
			Lang,
			{title: string; description: string; keywords: string}
		>;
		let metaKey = weatherByWeekday[lang as Lang];
		const selectedDay = days[lang as Lang][route.data.selectedWeekday];
		const selectedDayName = dayNames[lang as Lang][route.data.selectedWeekday] || '';
		const selectedNearestWeekday = nearestWeekday[lang as Lang][route.data.selectedWeekday] || '';

		// TODO temporary test
		if (domainName === 'sinoptik.ua' && location.id === 'ivano-frankivsk' && route.data.selectedWeekday === 6 && 'weatherSaturdayTest' in seo) {
			metaKey = (seo.weatherSaturdayTest as any)[lang as Lang];
		}

		// TODO temporary test
		if (domainName === 'sinoptik.ua' && location.id === 'ivano-frankivsk' && route.data.selectedWeekday === 0 && 'weatherSundayTest' in seo) {
			metaKey = (seo.weatherSundayTest as any)[lang as Lang];
		}

		return {
			title: interpolate(metaKey.title, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				countryTitle: location.superior.country?.title || '',
				regionTitle: location.superior.region?.title || '',
				day: selectedDay,
				nearestWeekday: selectedNearestWeekday,
			}),
			description: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				countryTitle: location.superior.country?.title || '',
				regionTitle: location.superior.region?.title || '',
				day: selectedDay,
			}),
			descriptionOg: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				countryTitle: location.superior.country?.title || '',
				regionTitle: location.superior.region?.title || '',
				day: selectedDay,
			}),
			keywords: interpolate(metaKey.keywords, {
				day: selectedDay,
				dayName: selectedDayName,
			}),
		};
	} else if (route.name === RouteName.weather && route.data.daysMode === ForecastDaysMode.Days7) {
		let metaKey = seo.weather7days[lang as keyof typeof seo.weather7days];

		// TODO temporary test
		if (domainName === 'sinoptik.ua' && location.id === 'ivano-frankivsk' && 'weather7daysTest' in seo) {
			metaKey = (seo.weather7daysTest as any)[lang as Lang];
		}

		return {
			title: interpolate(metaKey.title, {
				cityTitleIn: location.titleLc,
				countryTitle: location.superior.country?.title || '',
			}),
			description: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				countryTitle: location.superior.country?.title || '',
				regionTitleIn: location.superior.region?.titleLc || '',
			}),
			descriptionOg: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				countryTitle: location.superior.country?.title || '',
				regionTitleIn: location.superior.region?.titleLc || '',
			}),
			keywords: interpolate(metaKey.keywords, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
			}),
		};
	} else if (
		route.name === RouteName.weather &&
		route.data.daysMode === ForecastDaysMode.Days10 &&
		route.data.selectedDate &&
		'weatherByDate' in seo
	) {
		const weatherByDate = seo.weatherByDate as Record<Lang, {title: string; description: string; keywords: string}>;
		const metaKey = weatherByDate[lang as Lang];
		let selectedDate = new Date(route.data.selectedDate);
		selectedDate = new Date(selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60 * 1000);

		const selectedDateDay = selectedDate.getDate();
		const selectedDayIndex = selectedDate.getDay();
		const selectedMonthIndex = selectedDate.getMonth();
		const selectedMonthName = months[lang as Lang][selectedMonthIndex] || '';
		const selectedDay = days[lang as Lang][selectedDayIndex] || '';
		const selectedDayName = dayNames[lang as Lang][selectedDayIndex] || '';
		const rangeDate = `${selectedDateDay} ${selectedMonthName}`;
		const formattedDate = `${String(selectedDate.getDate()).padStart(2, '0')}/${String(
			selectedDate.getMonth() + 1,
		).padStart(2, '0')}/${selectedDate.getFullYear()}`;

		return {
			title: interpolate(metaKey.title, {
				cityTitle: location.title,
				cityTitleIn: location.titleLc,
				countryTitle: location.superior.country?.title || '',
				regionTitle: location.superior.region?.title || '',
				rangeDate,
				date: formattedDate,
				day: selectedDay,
				dayName: selectedDayName,
			}),
			description: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				regionTitle: location.superior.region?.title || '',
				districtTitle: location.superior.district?.title || '',
				countryTitle: location.superior.country?.title || '',
				rangeDate,
				date: formattedDate,
				day: selectedDay,
				dayName: selectedDayName,
			}),
			descriptionOg: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				regionTitle: location.superior.region?.title || '',
				districtTitle: location.superior.district?.title || '',
				countryTitle: location.superior.country?.title || '',
				rangeDate,
				date: formattedDate,
				day: selectedDay,
				dayName: selectedDayName,
			}),
			keywords: interpolate(metaKey.keywords, {
				cityTitleIn: location.titleLc,
				rangeDate,
				date: formattedDate,
				day: selectedDay,
				dayName: selectedDayName,
			}),
		};
	} else if (
		route.name === RouteName.weather &&
		route.data.daysMode === ForecastDaysMode.Days10 &&
		'weather10days' in seo
	) {
		const weather10days = seo.weather10days as Record<Lang, {title: string; description: string; keywords: string}>;
		let metaKey = weather10days[lang as Lang];
		const separatedDataTitles = [
			location.superior.country?.title,
			location.superior.region?.title,
			location.superior.district?.title,
			location.superior.subDistrict?.title,
		]
			.filter(Boolean)
			.join(', ');

		// TODO temporary test
		if (domainName === 'sinoptik.ua' && location.id === 'ivano-frankivsk' && 'weather10daysTest' in seo) {
			metaKey = (seo.weather10daysTest as any)[lang as Lang];
		}

		return {
			title: interpolate(metaKey.title, {
				cityTitleIn: location.titleLc,
				countryTitle: location.superior.country?.title || '',
				countryTitleIn: location.superior.country?.titleLc || '',
				cityTitle: location.title,
				regionTitle: location.superior.region?.title || '',
				regionTitleIn: location.superior.region?.titleLc || '',
			}),
			description: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				regionTitle: location.superior.region?.title || '',
				countryTitle: location.superior.country?.title || '',
				separatedDataTitles,
			}),
			descriptionOg: interpolate(metaKey.description, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
				regionTitle: location.superior.region?.title || '',
				countryTitle: location.superior.country?.title || '',
				separatedDataTitles,
			}),
			keywords: interpolate(metaKey.keywords, {
				cityTitleIn: location.titleLc,
				cityTitle: location.title,
			}),
		};
	}

	return seo.weatherMain[lang as keyof typeof seo.weatherMain];
}
